import {forwardRef, useImperativeHandle, useMemo, useRef} from 'react';
import Flatpickr, {DateTimePickerProps} from 'react-flatpickr';
import {setDateFormat} from '../helpers/dateUtils';
import {FuelSize} from '../types/general';
import {DatePickerContainer} from './DatePickerContainer';

export type FlatpickrOptions = NonNullable<DateTimePickerProps['options']>;

/**
 * NOTE: Weird work around for node keys like ATTRIBUTE_NODE not to be
 * passed. No idea why and where the Node Interface was passed, but here
 * we are...
 */
export type ModifiedDateTimePickerProps = Omit<DateTimePickerProps, keyof Node | 'showMonths' | 'defaultDate' | 'defaultValue' | 'size' | 'options'>;
/**
 * We're exposing this props straight from the flatpickrs options to
 * the developer. Now a dev can just `enableTime` straight from the
 * React component.
 */
export type ModifiedFlatpickrOptions = Partial<Pick<FlatpickrOptions, 'inline' | 'dateFormat' | 'noCalendar' | 'mode' | 'enableTime' | 'minDate' | 'maxDate'>>;

export interface BaseDatePickerProps extends ModifiedDateTimePickerProps, ModifiedFlatpickrOptions {
    defaultValue?: FlatpickrOptions['defaultDate'];
    value?: FlatpickrOptions['defaultDate'];
    /**
     * Enable dual calendar view.
     * @default false
     */
    dualMonth?: boolean;
    /**
     * Exposed underlying flatpickr options.
     * @default undefined
     */
    flatpickrOptions?: FlatpickrOptions;
    /**
     * Toggle dense calendar
     * @default false
     */
    isDense?: boolean;
    /**
     * Determines the size of the Input(s)
     * @default 'medium'
     */
    size?: FuelSize;
}

// TODO: Temp type fix workaround
export type BaseDatePickerRef = Flatpickr['flatpickr'] | null;

export const BaseDatePicker = forwardRef<BaseDatePickerRef, BaseDatePickerProps>(({
    defaultValue, dualMonth = false, inline, dateFormat: userDateFormat, noCalendar = false,
    enableTime = false, size, isDense = false, value, mode = 'single', className,
    minDate, maxDate, flatpickrOptions, onChange, ...props
}, ref) => {
    const fpRef = useRef<Flatpickr>(null);

    const showMonths = dualMonth ? 2 : 1;
    const dateFormat = userDateFormat || setDateFormat(enableTime, noCalendar);
    const hideYear = useMemo<boolean>(() => {
        if (minDate && maxDate) {
            const min = new Date(Date.parse(String(minDate)));
            const max = new Date(Date.parse(String(maxDate)));

            return min.getFullYear() === max.getFullYear();
        }

        return false;
    }, [minDate, maxDate]);

    useImperativeHandle(ref, () => {
        return fpRef.current!.flatpickr;
    }, [fpRef]);

    return (
        <DatePickerContainer
            hideYear={hideYear}
            inline={inline}
            isDense={isDense}
            showMonths={showMonths}
        >
            <Flatpickr
                className={className}
                {...props}
                options={{
                    allowInput: true,
                    ariaDateFormat: dateFormat,
                    enableTime,
                    minDate,
                    maxDate,
                    dateFormat,
                    inline,
                    mode,
                    monthSelectorType: 'dropdown',
                    noCalendar,
                    showMonths,
                    static: true,
                    onChange,
                    disableMobile: true,
                    ...flatpickrOptions
                }}
                ref={fpRef}
                value={value}
            />
        </DatePickerContainer>
    );
});

BaseDatePicker.displayName = 'BaseDatePicker';
